/**
 * Universal sync client для generic /sync/v2/{entity} endpoint backend.
 *
 * Backend ожидает в каждом record поля верхнего уровня:
 *   - id (string)
 *   - teacherId (string, для verification против JWT)
 *   - updatedAt (long)
 *   - ...остальные поля сущности (плоско, backend хранит как JSONB)
 *
 * LWW conflict resolution: при push backend применяет только записи
 * с updatedAt > existing.updatedAt (см. GenericSyncService.push).
 *
 * Возвращаемые pull-records содержат payload как сохранённый объект (с обёрткой
 * {id, updatedAt, payload} от старого формата, либо плоский — зависит от того,
 * как пушил клиент). Мы отдаём плоско и парсим плоско.
 */
import { api, currentTeacherIdStorage } from './client.ts'
import i18n from 'i18next'
import type {
  EntityType,
  SyncEntity,
  SyncPullResponse,
} from '../types/entities'

export const syncApi = {
  /** Push records на backend. Backend применит только те, у которых updatedAt > existing. */
  async push<T extends SyncEntity>(entity: EntityType, records: T[]): Promise<{ accepted: number; rejected: number; rejectedIds: string[] }> {
    const teacherId = currentTeacherIdStorage.get()
    if (!teacherId) throw new Error(i18n.t('apiSync.noTeacherId'))

    const req = {
      records: records.map((r) => ({
        ...r,
        teacherId,
      })),
    }
    return api(`/sync/v2/${entity}/push`, {
      method: 'POST',
      body: JSON.stringify(req),
    })
  },

  /** Pull всех записей сущности (с учётом since для incremental). */
  async pull<T extends SyncEntity>(
    entity: EntityType,
    since = 0,
    limit = 1000,
  ): Promise<SyncPullResponse<T>> {
    return api(`/sync/v2/${entity}/pull?since=${since}&limit=${limit}`)
  },
}

/** Сгенерировать новый UUID v4 (стандарт ES2025 — есть везде в современных браузерах). */
export function newId(): string {
  return crypto.randomUUID()
}

/** Текущее время в ms (для createdAt/updatedAt). */
export function now(): number {
  return Date.now()
}

/**
 * Создание новой сущности с заполненными базовыми полями (id, createdAt, updatedAt).
 * Принимает Partial<T>, чтобы можно было создавать без id.
 * Возвращает T (полностью валидный объект с обязательными полями SyncEntity).
 *
 * Использует truthy-check (||) а не nullish (??) — потому что blank-шаблоны
 * сущностей имеют id:'' и createdAt:0, которые должны быть заменены, не сохранены.
 */
export function makeEntity<T extends SyncEntity>(partial: Partial<T>): T {
  const ts = now()
  return {
    ...partial,
    id: partial.id || newId(),
    createdAt: partial.createdAt || ts,
    updatedAt: partial.updatedAt || ts,
  } as T
}
